import { useState, useEffect } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceDot, ReferenceLine, Area, ComposedChart
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendingUp, Ruler, Scale } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label, type }) => {
  if (!active || !payload?.length) return null;
  const unit = type === "height" ? "cm" : "kg";
  return (
    <div className="bg-[#1a1a2e]/95 backdrop-blur-md border border-white/10 rounded-xl p-3 shadow-2xl">
      <p className="text-xs font-semibold text-white mb-2">Age: {label} years</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-white/50">{p.name}:</span>
          <span className="text-white font-semibold">{p.value} {unit}</span>
        </div>
      ))}
    </div>
  );
};

const GrowthCurveChart = ({ heightResult, boneResult }) => {
  const [curves, setCurves] = useState(null);
  const [chartTab, setChartTab] = useState("height");
  const [loading, setLoading] = useState(true);

  const gender = heightResult?.name ? (boneResult?.sex === 1 ? "female" : "male") : "male";
  const resolvedGender = heightResult?.gender || gender;

  useEffect(() => {
    const fetchCurves = async () => {
      try {
        const res = await axios.get(`${API}/boneah/growth-curves/${resolvedGender}`);
        setCurves(res.data);
      } catch (e) {
        console.error("Failed to load growth curves", e);
      } finally {
        setLoading(false);
      }
    };
    fetchCurves();
  }, [resolvedGender]);

  if (loading) {
    return (
      <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl">
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-white/30 text-sm">Loading growth curves...</div>
        </CardContent>
      </Card>
    );
  }

  if (!curves) return null;

  const chronAge = heightResult?.chronological_age;
  const boneAge = heightResult?.bone_age;
  const currentHeight = heightResult?.present_height ?? heightResult?.current_height;
  const predictedHeight = heightResult?.ahp_xplusp;
  const currentWeight = heightResult?.present_weight;
  const predictedWeight = heightResult?.predicted_weight;
  const aphv = heightResult?.aphv;

  // Growth spurt window (APHV +/- 1.5 years)
  const spurtStart = aphv ? Math.round(aphv - 1.5) : (resolvedGender === "male" ? 11 : 9);
  const spurtEnd = aphv ? Math.round(aphv + 1) : (resolvedGender === "male" ? 15 : 13);

  // Add growth spurt marker to curve data
  const addSpurtZone = (data) => data.map(d => ({
    ...d,
    spurt: (d.age >= spurtStart && d.age <= spurtEnd) ? d.p97 : null,
  }));

  const percentileColors = {
    p3:  "rgba(239,68,68,0.55)",    // red
    p10: "rgba(249,115,22,0.55)",   // orange
    p25: "rgba(250,204,21,0.55)",   // yellow
    p50: "#4ECDC4",                 // teal median line
    p75: "rgba(34,197,94,0.55)",    // green
    p90: "rgba(59,130,246,0.55)",   // blue
    p97: "rgba(139,92,246,0.55)",   // violet
  };
  const percentileNames = {
    p3:  "3rd %ile",
    p10: "10th %ile",
    p25: "25th %ile",
    p50: "50th %ile",
    p75: "75th %ile",
    p90: "90th %ile",
    p97: "97th %ile",
  };

  const renderChart = (rawData, type) => {
    const unit = type === "height" ? "cm" : "kg";
    const childVal = type === "height" ? currentHeight : currentWeight;
    const predicted = type === "height" ? predictedHeight : predictedWeight;
    const data = addSpurtZone(rawData);

    return (
      <div className="h-[400px]" data-testid={`growth-curve-${type}`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 10 }}>
            <defs>
              <linearGradient id={`band-${type}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A855F7" stopOpacity={0.08} />
                <stop offset="50%" stopColor="#4ECDC4" stopOpacity={0.05} />
                <stop offset="100%" stopColor="#FF6B6B" stopOpacity={0.08} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              dataKey="age" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
              label={{ value: "Age (years)", position: "insideBottom", offset: -5,
                       fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
              label={{ value: `${type === "height" ? "Height" : "Weight"} (${unit})`,
                       angle: -90, position: "insideLeft", offset: 5,
                       fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip type={type} />} />

            {/* Percentile band fill */}
            <Area type="monotone" dataKey="p97" stroke="none" fill={`url(#band-${type})`} fillOpacity={1} />

            {/* Growth spurt zone */}
            <Area type="monotone" dataKey="spurt" stroke="none" fill="rgba(255,230,109,0.15)" fillOpacity={1} />

            {/* Percentile lines */}
            {Object.entries(percentileColors).map(([key, color]) => (
              <Line key={key} type="monotone" dataKey={key} name={percentileNames[key]}
                stroke={color} strokeWidth={key === "p50" ? 2.5 : 1.5}
                strokeDasharray={key === "p50" ? "0" : "6 3"}
                dot={false} activeDot={false} />
            ))}

            {/* Child's current position */}
            {chronAge && childVal && (
              <ReferenceDot x={Math.round(chronAge)} y={childVal} r={8}
                fill="#FF6B6B" stroke="#ffffff" strokeWidth={2}
                label={{ value: `${childVal}${unit}`, position: "top", fill: "#FF6B6B",
                         fontSize: 11, fontWeight: "bold", offset: 12 }}
              />
            )}

            {/* Bone age position */}
            {boneAge && childVal && Math.round(boneAge) !== Math.round(chronAge) && (
              <ReferenceDot x={Math.round(boneAge)} y={childVal} r={6}
                fill="#FFE66D" stroke="#ffffff" strokeWidth={2}
                label={{ value: "Bone Age", position: "bottom", fill: "#FFE66D",
                         fontSize: 10, offset: 12 }}
              />
            )}

            {/* Predicted adult height/weight */}
            {type === "height" && predicted && (
              <ReferenceDot x={18} y={predicted} r={7}
                fill="#4ECDC4" stroke="#ffffff" strokeWidth={2}
                label={{ value: `${predicted}cm`, position: "left", fill: "#4ECDC4",
                         fontSize: 11, fontWeight: "bold", offset: 12 }}
              />
            )}

            {/* APHV marker */}
            {aphv && (
              <ReferenceLine x={Math.round(aphv)} stroke="rgba(255,230,109,0.7)"
                strokeDasharray="4 4" strokeWidth={2}
                label={{ value: `APHV: ${aphv}y`, position: "top",
                fill: "#FFE66D", fontSize: 10, fontWeight: "bold" }} />
            )}

            {/* Reference lines */}
            {chronAge && (
              <ReferenceLine x={Math.round(chronAge)} stroke="rgba(255,107,107,0.4)"
                strokeDasharray="4 4" label={{ value: `CA: ${chronAge}y`, position: "top",
                fill: "rgba(255,107,107,0.6)", fontSize: 10 }} />
            )}
            {boneAge && Math.round(boneAge) !== Math.round(chronAge) && (
              <ReferenceLine x={Math.round(boneAge)} stroke="rgba(255,230,109,0.4)"
                strokeDasharray="4 4" label={{ value: `BA: ${boneAge}y`, position: "top",
                fill: "rgba(255,230,109,0.6)", fontSize: 10 }} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl" data-testid="growth-curve-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-display">
          <TrendingUp className="w-5 h-5 text-[#4ECDC4]" /> Interactive Growth Curves
        </CardTitle>
        <p className="text-white/40 text-sm">
          {(() => {
            const g = resolvedGender === "male" ? "boys" : "girls";
            const age = chronAge;
            if (age != null && age < 5) {
              return `WHO MGRS 2006 percentiles for ${g} (0-5 years)`;
            }
            if (age != null && age >= 5) {
              return `IAP-Khadilkar 2015 percentiles for ${g} (5-18 years)`;
            }
            return `IAP-Khadilkar 2015 (5-18 y) · WHO MGRS (<5 y) percentiles for ${g}`;
          })()}
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={chartTab} onValueChange={setChartTab}>
          <TabsList className="bg-white/[0.03] p-1 rounded-xl border border-white/10 mb-4 w-full grid grid-cols-2">
            <TabsTrigger value="height"
              className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-[#0a1628] text-white/50"
              data-testid="chart-tab-height">
              <Ruler className="w-4 h-4 mr-2" /> Height-for-Age
            </TabsTrigger>
            <TabsTrigger value="weight"
              className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-[#0a1628] text-white/50"
              data-testid="chart-tab-weight">
              <Scale className="w-4 h-4 mr-2" /> Weight-for-Age
            </TabsTrigger>
          </TabsList>

          <TabsContent value="height">
            {renderChart(curves.height_curves, "height")}
          </TabsContent>
          <TabsContent value="weight">
            {renderChart(curves.weight_curves, "weight")}
          </TabsContent>
        </Tabs>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pt-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#FF6B6B] ring-2 ring-white/30" />
            <span className="text-xs text-white/50">Present Value (at Chronological Age)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#FFE66D] ring-2 ring-white/30" />
            <span className="text-xs text-white/50">Position at Bone Age</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#4ECDC4] ring-2 ring-white/30" />
            <span className="text-xs text-white/50">Predicted Final (Age 18)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 bg-[#4ECDC4]" />
            <span className="text-xs text-white/50">50th %ile</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 border-t-2 border-dashed border-white/30" />
            <span className="text-xs text-white/50">Other %iles</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-3 rounded-sm bg-[#FFE66D]/20 border border-[#FFE66D]/40" />
            <span className="text-xs text-white/50">Growth Spurt Zone</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GrowthCurveChart;
